import {
  ChevronDown,
  Copy,
  History,
  KeyRound,
  Layers3,
  Pencil,
  Plus,
  QrCode,
  RefreshCw,
  Trash2,
  X,
} from 'lucide-react';
import { AnimatePresence, motion } from 'motion/react';
import { type FormEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { api } from '../api';
import { SortableList } from '../components/SortableList';
import {
  Button,
  EmptyState,
  Field,
  IconButton,
  Modal,
  Select,
  Spinner,
  Status,
  useToast,
} from '../components/ui';
import { errorMessage, formatDate, INTERVALS } from '../lib';
import type { Collection, CollectionRefreshRun, RuleSet, Source } from '../types';

const MAX_COLLECTION_SOURCES = 20;

interface CollectionDraft {
  name: string;
  sourceIds: string[];
  ruleSetId: string;
  refreshIntervalMinutes: number;
}

const emptyDraft: CollectionDraft = {
  name: '',
  sourceIds: [],
  ruleSetId: '',
  refreshIntervalMinutes: 360,
};

export function CollectionsPage() {
  const [collections, setCollections] = useState<Collection[] | null>(null);
  const [sources, setSources] = useState<Source[]>([]);
  const [ruleSets, setRuleSets] = useState<RuleSet[]>([]);
  const [draft, setDraft] = useState<CollectionDraft>(emptyDraft);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [pending, setPending] = useState<Set<string>>(new Set());
  const [refreshing, setRefreshing] = useState<Set<string>>(new Set());
  const [expandedCollections, setExpandedCollections] = useState<Set<string>>(new Set());
  const [qr, setQr] = useState<{ name: string; url: string; image: string } | null>(null);
  const [runs, setRuns] = useState<{ name: string; items: CollectionRefreshRun[] } | null>(null);
  const initialSnapshot = useRef('');
  const { notify } = useToast();

  const load = useCallback(async () => {
    const [nextCollections, nextSources, nextRuleSets] = await Promise.all([
      api.get<Collection[]>('/api/collections'),
      api.get<Source[]>('/api/sources'),
      api.get<RuleSet[]>('/api/rule-sets'),
    ]);
    setCollections(nextCollections);
    setSources(nextSources);
    setRuleSets(nextRuleSets);
  }, []);
  useEffect(() => { load().catch((error) => notify(errorMessage(error), 'error')); }, [load, notify]);

  const sourceMap = useMemo(() => new Map(sources.map((source) => [source.id, source])), [sources]);
  const ruleMap = useMemo(() => new Map(ruleSets.map((ruleSet) => [ruleSet.id, ruleSet.name])), [ruleSets]);
  const selected = draft.sourceIds.map((id) => sourceMap.get(id)).filter((source): source is Source => Boolean(source));
  const snapshot = (value: CollectionDraft) => JSON.stringify(value);
  const openCreate = () => {
    const next = { ...emptyDraft, sourceIds: [] };
    setEditingId(null);
    setDraft(next);
    initialSnapshot.current = snapshot(next);
    setOpen(true);
  };
  const openEdit = (collection: Collection) => {
    const next = {
      name: collection.name,
      sourceIds: collection.sourceIds,
      ruleSetId: collection.ruleSetId ?? '',
      refreshIntervalMinutes: collection.refreshIntervalMinutes,
    };
    setEditingId(collection.id);
    setDraft(next);
    initialSnapshot.current = snapshot(next);
    setOpen(true);
  };
  const toggleSource = (id: string) => setDraft((current) => {
    if (current.sourceIds.includes(id)) {
      return { ...current, sourceIds: current.sourceIds.filter((value) => value !== id) };
    }
    if (current.sourceIds.length >= MAX_COLLECTION_SOURCES) {
      notify(`一个组合最多包含 ${MAX_COLLECTION_SOURCES} 个订阅源`, 'error');
      return current;
    }
    return { ...current, sourceIds: [...current.sourceIds, id] };
  });

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    const body = {
      name: draft.name,
      sourceIds: draft.sourceIds,
      ruleSetId: draft.ruleSetId || null,
      refreshIntervalMinutes: draft.refreshIntervalMinutes,
    };
    try {
      if (editingId) await api.put(`/api/collections/${editingId}`, body);
      else await api.post('/api/collections', body);
      notify(editingId ? '组合已更新' : '组合已创建');
      setOpen(false);
      await load();
    } catch (error) {
      notify(errorMessage(error), 'error');
    } finally {
      setBusy(false);
    }
  };

  const guard = async (id: string, action: () => Promise<void>) => {
    if (pending.has(id)) return;
    setPending((current) => new Set(current).add(id));
    try { await action(); } finally {
      setPending((current) => { const next = new Set(current); next.delete(id); return next; });
    }
  };
  const copy = async (collection: Collection) => {
    if (!collection.publicUrl) return notify('组合令牌不可用，请先轮换令牌', 'error');
    try { await navigator.clipboard.writeText(collection.publicUrl); notify('Provider 地址已复制'); }
    catch { notify('浏览器拒绝访问剪贴板', 'error'); }
  };
  const showQr = async (collection: Collection) => {
    if (!collection.publicUrl) return notify('组合令牌不可用，请先轮换令牌', 'error');
    try {
      const { default: QRCode } = await import('qrcode');
      setQr({
        name: collection.name,
        url: collection.publicUrl,
        image: await QRCode.toDataURL(collection.publicUrl, {
          width: 320,
          margin: 2,
          color: { dark: '#18201f', light: '#ffffff' },
        }),
      });
    } catch (error) { notify(errorMessage(error), 'error'); }
  };
  const refresh = async (collection: Collection) => {
    setRefreshing((current) => new Set(current).add(collection.id));
    try {
      await api.post(`/api/collections/${collection.id}/refresh`);
      notify('组合 Provider 已刷新');
      await load();
    } catch (error) {
      notify(errorMessage(error), 'error');
      await load();
    } finally {
      setRefreshing((current) => { const next = new Set(current); next.delete(collection.id); return next; });
    }
  };
  const history = async (collection: Collection) => {
    try {
      setRuns({
        name: collection.name,
        items: await api.get<CollectionRefreshRun[]>(`/api/collections/${collection.id}/runs`),
      });
    } catch (error) { notify(errorMessage(error), 'error'); }
  };
  const rotate = (collection: Collection) => {
    if (!window.confirm('轮换后旧 Provider 地址会立即失效，继续吗？')) return;
    return guard(collection.id, async () => {
      try {
        await api.post(`/api/collections/${collection.id}/rotate-token`);
        notify('令牌已轮换，旧地址已失效');
        await load();
      } catch (error) { notify(errorMessage(error), 'error'); }
    });
  };
  const remove = (collection: Collection) => {
    if (!window.confirm(`删除组合“${collection.name}”及其 Provider 产物？`)) return;
    return guard(collection.id, async () => {
      try { await api.delete(`/api/collections/${collection.id}`); notify('组合已删除'); await load(); }
      catch (error) { notify(errorMessage(error), 'error'); }
    });
  };
  const toggleSources = (id: string) => setExpandedCollections((current) => {
    const next = new Set(current);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    return next;
  });

  return <>
    <header className="page-header"><div><span className="eyebrow">公开 Provider</span><h1>组合</h1><p>管理有序订阅源与 Mihomo Provider 地址。</p></div><Button icon={<Plus size={17} />} onClick={openCreate} disabled={sources.length === 0}>新建组合</Button></header>
    {!collections ? <Spinner /> : collections.length === 0 ? <EmptyState icon={<Layers3 size={24} />} title={sources.length ? '还没有订阅组合' : '请先创建至少一个订阅源'} action={sources.length ? <Button icon={<Plus size={16} />} onClick={openCreate}>创建组合</Button> : undefined} /> : <section className="publication-list">{collections.map((collection, index) => {
      const sourceNames = collection.sourceIds.map((id) => sourceMap.get(id)?.name ?? '已删除来源');
      const expanded = expandedCollections.has(collection.id);
      const collapsible = sourceNames.length > 3;
      const visibleSourceNames = expanded ? sourceNames : sourceNames.slice(0, 3);
      const sourceListId = `collection-${collection.id}-sources`;
      const metadata = `${collection.ruleSetId ? ruleMap.get(collection.ruleSetId) ?? '规则已删除' : '无规则链'} · ${collection.lastSuccessAt ? `上次成功 ${formatDate(collection.lastSuccessAt)}` : `计划刷新 ${formatDate(collection.nextRefreshAt)}`}`;
      return <motion.article className="publication-row collection-row" key={collection.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: Math.min(index * 0.045, 0.22), duration: 0.24 }}>
        <div className="publication-status"><Status value={refreshing.has(collection.id) ? 'refreshing' : collection.status} /></div>
        <div className="publication-main collection-main">
          <div className="item-title"><h2>{collection.name}</h2><span className="tag">{collection.sourceIds.length} 个来源</span></div>
          <div className="collection-detail-row">
            <motion.p key={expanded ? 'expanded' : 'collapsed'} id={sourceListId} className="collection-source-text" title={sourceNames.join(' · ')} initial={{ opacity: 0, x: expanded ? 6 : -6 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.18 }}>
              {visibleSourceNames.join(' · ')}
            </motion.p>
            {collapsible && <motion.button type="button" className="collection-source-toggle" aria-expanded={expanded} aria-controls={sourceListId} aria-label={expanded ? '收起来源' : '展开全部来源'} onClick={() => toggleSources(collection.id)} animate={{ rotate: expanded ? 180 : 0 }} transition={{ duration: 0.2 }}>
              <ChevronDown size={14} />
            </motion.button>}
            <AnimatePresence initial={false}>{!expanded && <motion.small key="metadata" className="collection-meta" initial={{ opacity: 0, x: 6 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -6 }} transition={{ duration: 0.18 }}>
              {metadata}
            </motion.small>}</AnimatePresence>
          </div>
          {collection.lastError && <div className="row-error">{collection.lastError}</div>}
        </div>
        <div className="publication-actions"><IconButton label="复制 Provider 地址" onClick={() => void copy(collection)}><Copy size={17} /></IconButton><IconButton label="显示二维码" onClick={() => void showQr(collection)}><QrCode size={17} /></IconButton><IconButton label="立即刷新" disabled={refreshing.has(collection.id)} onClick={() => void refresh(collection)}><RefreshCw size={17} className={refreshing.has(collection.id) ? 'spinning' : ''} /></IconButton><IconButton label="刷新记录" onClick={() => void history(collection)}><History size={17} /></IconButton><IconButton label="编辑" onClick={() => openEdit(collection)}><Pencil size={17} /></IconButton><IconButton label="轮换令牌" disabled={pending.has(collection.id)} onClick={() => void rotate(collection)}><KeyRound size={17} /></IconButton><IconButton label="删除" disabled={pending.has(collection.id)} onClick={() => void remove(collection)}><Trash2 size={17} /></IconButton></div>
      </motion.article>;
    })}</section>}

    <Modal open={open} title={editingId ? '编辑组合' : '新建组合'} onClose={() => setOpen(false)} wide isDirty={() => snapshot(draft) !== initialSnapshot.current}>
      <form className="form-stack" onSubmit={submit}>
        <Field label="组合名称"><input value={draft.name} onChange={(event) => setDraft({ ...draft, name: event.target.value })} required maxLength={80} /></Field>
        <div className="split-fields"><Field label="规则链"><Select value={draft.ruleSetId} onChange={(event) => setDraft({ ...draft, ruleSetId: event.target.value })}><option value="">不使用规则</option>{ruleSets.map((ruleSet) => <option key={ruleSet.id} value={ruleSet.id}>{ruleSet.name}</option>)}</Select></Field><Field label="刷新周期"><Select value={draft.refreshIntervalMinutes} onChange={(event) => setDraft({ ...draft, refreshIntervalMinutes: Number(event.target.value) })}>{INTERVALS.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}</Select></Field></div>
        <fieldset className="check-grid"><legend>选择订阅源（{draft.sourceIds.length} / {MAX_COLLECTION_SOURCES}）</legend>{sources.map((source) => { const checked = draft.sourceIds.includes(source.id); return <label key={source.id} className={checked ? 'checked' : ''}><input type="checkbox" checked={checked} disabled={!checked && draft.sourceIds.length >= MAX_COLLECTION_SOURCES} onChange={() => toggleSource(source.id)} /><span>{source.name}</span><small>{source.type === 'remote' ? '远程' : '手动'}</small></label>; })}</fieldset>
        {selected.length > 0 && <div className="order-editor"><span className="field-label">合并顺序</span><SortableList items={selected} onChange={(items) => setDraft({ ...draft, sourceIds: items.map((item) => item.id) })} render={(source) => <div className="sortable-row"><span>{source.name}</span><IconButton label="移除" onClick={() => toggleSource(source.id)}><X size={15} /></IconButton></div>} /></div>}
        <div className="form-actions"><Button type="button" variant="secondary" onClick={() => setOpen(false)}>取消</Button><Button type="submit" disabled={busy || draft.sourceIds.length === 0}>{busy ? '保存中...' : '保存组合'}</Button></div>
      </form>
    </Modal>

    <Modal open={Boolean(qr)} title={qr?.name ?? 'Provider 二维码'} onClose={() => setQr(null)}>{qr && <div className="qr-panel"><img src={qr.image} alt={`${qr.name} Provider 二维码`} /><code>{qr.url}</code><Button variant="secondary" icon={<Copy size={16} />} onClick={() => navigator.clipboard.writeText(qr.url).then(() => notify('Provider 地址已复制'), () => notify('浏览器拒绝访问剪贴板', 'error'))}>复制地址</Button></div>}</Modal>
    <Modal open={Boolean(runs)} title={`${runs?.name ?? ''} · 刷新记录`} onClose={() => setRuns(null)} wide>{runs && (runs.items.length === 0 ? <div className="inline-empty">暂无刷新记录</div> : <div className="run-list">{runs.items.map((run) => <div key={run.id}><span className={run.status === 'success' ? 'run-ok' : 'run-fail'}>{run.status === 'success' ? '成功' : '失败'}</span><strong>{formatDate(run.createdAt)}</strong><span>{run.nodeCount === null ? '-' : `${run.nodeCount} 节点`}</span><span>{run.durationMs === null ? '-' : `${run.durationMs} ms`}</span>{run.error && <small>{run.error}</small>}</div>)}</div>)}</Modal>
  </>;
}
